import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Dashboard from "@/pages/Dashboard";
import Profile from "@/pages/Profile";
import OperatorHub from "@/pages/OperatorHub";
import { LayoutDashboard, User as UserIcon, IdCard, FileText, Sparkles, CreditCard, MailOpen, ArrowRight, Wallet, Radar, Loader2, Building2, Users, ShieldCheck } from "lucide-react";
import PjpWalletCard from "@/components/PjpWalletCard";
import FlashBadgeButton from "@/components/FlashBadgeButton";
import FlyingBuddies from "@/components/FlyingBuddies";
import { isPro } from "@/lib/permissions";

/**
 * Cockpit — Unified "one roof" pilot cockpit combining operational Dashboard
 * panels and personal Profile management under a single scroll with sticky
 * sidebar anchor navigation and an animated HUD background.
 *
 * Anchor sections:
 *   #overview  -> Dashboard operational panels
 *   #profile   -> Profile editor (identity, ratings, bio)
 *   #docs      -> Documents shortcut
 *   #id        -> Digital ID shortcut
 *   #membership -> Membership shortcut
 */
export default function Cockpit() {
  const { user } = useAuth();
  const [active, setActive] = useState("profile");
  const contentRef = useRef(null);

  // Charter Clients don't have crew credentials, ratings, or a pilot logbook —
  // their command center is /charter-dashboard (trip RFQs + operator bids).
  // A user who ALSO wears another hat (pilot+charter_client) still lands here.
  const roles = [user?.role, ...(user?.additional_roles || [])].filter(Boolean);
  const isCharterClientOnly = roles.length > 0 && roles.every((r) => r === "charter_client");

  // ═══════════════════════════════════════════════════════════════════════
  // Multi-role "View as" switcher.
  //
  // Users that carry more than one hat (e.g. pilot + charter_operator) can
  // flip the cockpit into any of their roles without changing the DB.
  // Admins get every standard role so we can QA each surface end-to-end
  // (also lets Jeff jump into "Operator" mode from his pilot profile).
  //
  // Only the cockpit UI reacts — role-related permissions on the API stay
  // driven by the persisted `user.role` value.
  // ═══════════════════════════════════════════════════════════════════════
  const ADMIN_ROLE_LIBRARY = ["pilot", "flight_attendant", "amt", "fbo", "charter_operator", "aircraft_owner", "aircraft_sales", "charter_client", "enthusiast"];
  const availableRoles = useMemo(() => {
    const own = [user?.role, ...(user?.additional_roles || [])].filter(Boolean);
    const set = new Set(own);
    if (user?.is_admin) ADMIN_ROLE_LIBRARY.forEach((r) => set.add(r));
    return Array.from(set);
  }, [user?.role, user?.additional_roles, user?.is_admin]);

  const [viewAs, setViewAs] = useState(user?.role || "pilot");
  useEffect(() => { if (user?.role) setViewAs(user.role); }, [user?.role]);

  const isOperator = viewAs === "charter_operator";

  // On mount / hash change, scroll to the requested anchor if the URL
  // includes one (e.g. /dashboard#profile from a header shortcut).
  useEffect(() => {
    const scrollToHash = () => {
      const id = window.location.hash?.replace("#", "");
      if (!id) return;
      // Wait a tick for embedded Dashboard/Profile to render first.
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    };
    scrollToHash();
    window.addEventListener("hashchange", scrollToHash);
    return () => window.removeEventListener("hashchange", scrollToHash);
  }, []);

  // Track which section is in view for the sidebar highlight.
  useEffect(() => {
    if (!contentRef.current) return;
    const sections = (isOperator
      ? ["profile", "ops", "wallet", "docs", "id", "membership"]
      : ["profile", "overview", "wallet", "docs", "id", "membership"])
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (sections.length === 0) return;

    const io = new IntersectionObserver(
      (entries) => {
        // Pick the section with the largest intersection ratio.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: [0, 0.15, 0.3, 0.5] }
    );
    sections.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, [user?.id, isOperator]);

  // If not authenticated, defer to Dashboard's auth-required screen.
  if (!user) return <Dashboard />;

  const jump = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Redirect Charter Client-only users AFTER all hooks — hooks must be called
  // unconditionally on every render (rules-of-hooks).
  if (isCharterClientOnly) {
    return <Navigate to="/charter-dashboard" replace />;
  }

  return (
    <div className="relative min-h-[calc(100vh-80px)]">
      {/* ==== Animated HUD background ==== */}
      <AliveHUD user={user} />

      {/* Mobile-only Flash Badge FAB — one-tap ID for FBOs on the go. */}
      <FlashBadgeButton variant="fab" testId="cockpit-flash-badge-fab" />

      <div className="relative z-[1] max-w-7xl mx-auto px-4 md:px-6 pt-6 md:pt-8 pb-16">
        <div className="grid lg:grid-cols-[220px_1fr] gap-6">
          {/* ==== Sticky sidebar nav ==== */}
          <aside className="hidden lg:block" data-testid="cockpit-sidebar">
            <div className="cockpit-nav">
              <AccountStatusBanner user={user} />
              {isOperator ? (
                <>
                  <NavItem id="profile"  active={active} onJump={jump} icon={UserIcon}    label="Profile" />
                  <NavItem id="ops"      active={active} onJump={jump} icon={Building2}   label="Operations Hub" />
                </>
              ) : (
                <>
                  <NavItem id="profile"  active={active} onJump={jump} icon={UserIcon}    label="Profile" />
                  <NavItem id="overview" active={active} onJump={jump} icon={LayoutDashboard} label="Overview" />
                </>
              )}
              <NavItem id="wallet"   active={active} onJump={jump} icon={Wallet}          label="Wallet" />
              <NavItem id="buddies"  active={active} onJump={jump} icon={Users}           label="Flying Buddies" />
              <NavItem id="docs"     active={active} onJump={jump} icon={FileText}        label="Documents" />
              <NavItem id="id"       active={active} onJump={jump} icon={IdCard}          label="Digital ID" />
              <NavItem id="membership" active={active} onJump={jump} icon={CreditCard}    label="Membership" />

              <div className="mt-4 pt-3 border-t border-white/[0.06] space-y-1">
                <div className="mb-2">
                  <FlashBadgeButton variant="compact" label="FLASH BADGE · FBO" testId="cockpit-flash-badge-sidebar" />
                </div>
                <Link to="/inbox" data-testid="cockpit-nav-inbox" className="cockpit-nav__item">
                  <span className="dot" /><MailOpen className="h-3.5 w-3.5" /> Inbox
                </Link>
                <Link to="/aerointel" data-testid="cockpit-nav-ai" className="cockpit-nav__item">
                  <span className="dot" /><Sparkles className="h-3.5 w-3.5" /> AeroIntel
                </Link>
              </div>
            </div>
          </aside>

          {/* ==== Main content column ==== */}
          <main ref={contentRef} className="min-w-0">
            {/* Mobile: account-status banner is first, then section chips */}
            <div className="lg:hidden mb-3">
              <AccountStatusBanner user={user} />
            </div>

            {/* Multi-role "View as" switcher — always renders when the user
                carries more than one hat (or is admin). Flips the cockpit
                between pilot / operator / etc without touching the DB. */}
            {availableRoles.length > 1 && (
              <RoleSwitcher
                roles={availableRoles}
                current={viewAs}
                onChange={setViewAs}
                nativeRole={user?.role}
              />
            )}

            {/* Mobile section chips */}
            <div className="lg:hidden flex gap-2 overflow-x-auto no-scrollbar pb-3 mb-2 -mx-4 px-4" data-testid="cockpit-mobile-nav">
              {(isOperator
                ? [
                    { id: "profile", label: "Profile" },
                    { id: "ops",     label: "Ops Hub" },
                    { id: "wallet",  label: "Wallet" },
                    { id: "docs",    label: "Docs" },
                    { id: "id",      label: "ID" },
                    { id: "membership", label: "Membership" },
                  ]
                : [
                    { id: "profile",    label: "Profile" },
                    { id: "overview",   label: "Overview" },
                    { id: "wallet",     label: "Wallet" },
                    { id: "buddies",    label: "Flying Buddies" },
                    { id: "docs",       label: "Docs" },
                    { id: "id",         label: "ID" },
                    { id: "membership", label: "Membership" },
                  ]).map((it) => (
                <a
                  key={it.id}
                  href={`#${it.id}`}
                  onClick={jump(it.id)}
                  data-testid={`cockpit-mobile-nav-${it.id}`}
                  className={`flex-shrink-0 mono text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-full border transition-colors ${
                    active === it.id
                      ? "border-cyan bg-cyan/15 text-white"
                      : "border-white/15 bg-white/[0.02] text-white/60"
                  }`}
                >
                  {it.label}
                </a>
              ))}
            </div>

            {isOperator ? (
              <>
                {/* Profile FIRST for operators */}
                <section id="profile" className="cockpit-section" data-testid="cockpit-section-profile">
                  <Profile
                    embedded
                    viewAs={viewAs}
                    onViewAs={setViewAs}
                    availableRoles={availableRoles}
                  />
                </section>

                {/* Operations Hub below the profile — fleet, empty legs, RFQ inbox, program */}
                <section id="ops" className="cockpit-section" data-testid="cockpit-section-ops">
                  <OperatorHub user={user} />
                </section>
              </>
            ) : (
              <>
                {/* Profile — leads for pilots so the identity/badge card is the
                    first thing they see when landing in the cockpit. */}
                <section id="profile" className="cockpit-section" data-testid="cockpit-section-profile">
                  <Profile
                    embedded
                    viewAs={viewAs}
                    onViewAs={setViewAs}
                    availableRoles={availableRoles}
                  />
                </section>

                {/* Overview — dashboard panels below the identity card */}
                <section id="overview" className="cockpit-section" data-testid="cockpit-section-overview">
                  <Dashboard embedded />
                  <JobRadarCard />
                </section>
              </>
            )}

            {/* PJP Wallet */}
            <section id="wallet" className="cockpit-section pt-4" data-testid="cockpit-section-wallet">
              <div className="mb-3">
                <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// PJP WALLET</div>
                <div className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight mt-1">Your in-house digital wallet</div>
                <div className="text-sm text-white/55 mt-0.5">Bonuses, referrals and empty-leg fills land here. Cash-out to your bank once Stripe Connect + ACH verification ship.</div>
              </div>
              <PjpWalletCard />
            </section>

            {/* Flying Buddies — friends/followers network */}
            {!isOperator && (
              <section id="buddies" className="cockpit-section pt-4" data-testid="cockpit-section-buddies">
                <FlyingBuddies />
              </section>
            )}

            {/* Documents shortcut */}
            <section id="docs" className="cockpit-section pt-4" data-testid="cockpit-section-docs">
              <ShortcutCard
                title="Documents"
                overline="// SECURE VAULT"
                blurb="Manage your ID, medical, licenses, insurance and resume. OCR-verified & fully encrypted."
                to="/documents"
                cta="Open Documents"
                testId="cockpit-docs-cta"
              />
            </section>

            {/* Digital ID — smart card. If the pilot already owns the badge
                we drop them straight into build / share; if not, we sell it. */}
            <section id="id" className="cockpit-section pt-4" data-testid="cockpit-section-id">
              <DigitalIdCard user={user} />
            </section>

            {/* Membership — live subscription state with cancel + auto-renew
                controls right on the cockpit. */}
            <section id="membership" className="cockpit-section pt-4 pb-6" data-testid="cockpit-section-membership">
              <MembershipCard user={user} />
            </section>
          </main>
        </div>
      </div>
    </div>
  );
}

/**
 * AccountStatusBanner — bright green PRO ACCOUNT pill for paid members, or a
 * cyan-outlined FREE pill with an inline Upgrade link for free members. Sits
 * at the top of the cockpit sidebar (and above the mobile nav chips) so
 * account state is unambiguously visible from anywhere in the cockpit.
 */
function AccountStatusBanner({ user }) {
  // Bright green banner ONLY for members who have actually paid (or were
  // grandfathered from a legacy paid tier). Admins and role-only accounts
  // fall through to the free-account variant so the badge stays honest.
  const PAID_TIERS = new Set(["pjp_pro", "charter_operator", "charter_client", "empty_leg_rider"]);
  const paidPro =
    !!user?.founding_member ||
    (user?.membership_status === "active" && PAID_TIERS.has(user?.membership_tier_slug));
  const founding = !!user?.founding_member;
  if (paidPro) {
    return (
      <Link
        to="/membership"
        data-testid="account-status-banner-pro"
        className="relative block rounded-lg overflow-hidden mb-3 group"
        style={{
          background: "linear-gradient(135deg, #24E37A 0%, #14B85F 100%)",
          boxShadow: "0 0 24px -4px rgba(46,213,115,0.65)",
        }}
      >
        {/* Sweeping shine */}
        <div className="absolute inset-y-0 -inset-x-4 opacity-40 pointer-events-none">
          <div className="h-full w-1/3 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-[scanline_3.2s_linear_infinite]" />
        </div>
        <div className="relative flex items-center gap-2 px-3 py-2">
          <span className="relative inline-flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-70 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-white shadow-[0_0_6px_rgba(255,255,255,0.9)]" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-display text-[13px] font-extrabold tracking-[0.14em] text-obsidian leading-none">
              PRO ACCOUNT
            </div>
            <div className="mono text-[9px] tracking-[0.24em] uppercase text-obsidian/70 mt-0.5">
              {founding ? "Founding Member" : "Full Access"}
            </div>
          </div>
          <Sparkles className="h-3.5 w-3.5 text-obsidian" />
        </div>
      </Link>
    );
  }
  return (
    <div
      data-testid="account-status-banner-free"
      className="relative rounded-lg mb-3 border border-cyan/35 bg-cyan/[0.06]"
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <span className="h-1.5 w-1.5 rounded-full bg-white/45" />
        <div className="flex-1 min-w-0">
          <div className="mono text-[10px] font-bold tracking-[0.22em] uppercase text-white/85 leading-none">
            FREE ACCOUNT
          </div>
          <div className="text-[10px] text-white/50 mt-0.5">
            Job Board · AeroIntel · JumpSeat locked
          </div>
        </div>
        <Link
          to="/membership/addon?tier=pro"
          data-testid="account-status-upgrade-link"
          className="mono text-[9px] tracking-widest uppercase text-cyan hover:text-white border border-cyan/40 hover:border-cyan hover:bg-cyan/[0.15] px-2 py-1 rounded-full transition-colors whitespace-nowrap"
        >
          Upgrade
        </Link>
      </div>
    </div>
  );
}


function NavItem({ id, active, onJump, icon: Icon, label }) {  const isActive = active === id;
  return (
    <a
      href={`#${id}`}
      onClick={onJump(id)}
      data-testid={`cockpit-nav-${id}`}
      className={`cockpit-nav__item ${isActive ? "active" : ""}`}
    >
      <span className="dot" />
      <Icon className="h-3.5 w-3.5" />
      {label}
    </a>
  );
}

function ShortcutCard({ title, overline, blurb, to, cta, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="group relative block rounded-2xl overflow-hidden isolate transition-transform hover:-translate-y-0.5"
    >
      {/* Ambient blur glow behind the glass */}
      <div
        aria-hidden="true"
        className="absolute -z-10 top-1/2 left-8 -translate-y-1/2 w-[240px] h-[240px] rounded-full opacity-30 pointer-events-none transition-opacity duration-500 group-hover:opacity-60"
        style={{ background: "radial-gradient(circle, rgba(75,156,255,0.55) 0%, transparent 70%)", filter: "blur(60px)" }}
      />
      <div
        className="relative rounded-2xl p-5 bg-white/[0.03] backdrop-blur-2xl backdrop-saturate-150 border border-white/10 group-hover:border-cyan/50 overflow-hidden hud-corners transition-colors"
        style={{
          boxShadow: "0 1px 0 rgba(255,255,255,0.05) inset, 0 20px 40px -20px rgba(0,0,0,0.5), 0 0 60px -32px rgba(75,156,255,0.55)",
        }}
      >
        {/* Top-edge light-catch */}
        <span
          aria-hidden="true"
          className="absolute top-0 left-5 right-5 h-px pointer-events-none opacity-70 group-hover:opacity-100 transition-opacity"
          style={{ background: "linear-gradient(90deg, transparent, rgba(75,156,255,0.85), transparent)" }}
        />
        <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">{overline}</div>
        <div className="flex items-center justify-between gap-4 mt-1 flex-wrap">
          <div className="min-w-0">
            <div className="font-display text-xl md:text-2xl font-bold text-white tracking-tight">{title}</div>
            <div className="text-sm text-white/60 mt-1 max-w-xl">{blurb}</div>
          </div>
          <span className="btn-hud btn-hud-solid flex-shrink-0 group-hover:brightness-110">
            {cta.toUpperCase()} <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}

/**
 * AliveHUD — animated background layer sitting behind cockpit content.
 * Pure CSS animations for performance. Corner readouts derive values from the
 * authed user object so it feels *alive* + personalized.
 */
function AliveHUD({ user }) {
  // Rolling timestamp/heading for HUD readouts — refresh every 4s so digits
  // change gently without hammering the render cycle.
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setTick(Date.now()), 4000);
    return () => clearInterval(t);
  }, []);

  const now = new Date(tick);
  const zulu = `${String(now.getUTCHours()).padStart(2, "0")}${String(now.getUTCMinutes()).padStart(2, "0")}Z`;
  const heading = String(((now.getUTCMinutes() * 6) + now.getUTCSeconds()) % 360).padStart(3, "0");
  const altitude = `FL${380 + (now.getUTCSeconds() % 12) * 2}`;
  const speed = `${430 + (now.getUTCSeconds() % 25)}KT`;

  const memberNum = user?.pjp_member_number
    ? String(user.pjp_member_number).padStart(5, "0")
    : "26511";

  return (
    <div className="cockpit-hud" aria-hidden="true">
      {/* Layer 1 — pulsing grid */}
      <div className="cockpit-hud__grid" />

      {/* Layer 2 — scan line sweep */}
      <div className="cockpit-hud__scanline" />

      {/* Layer 3 — corner data readouts */}
      <div className="cockpit-hud__readout cockpit-hud__readout--tl">
        <div className="k">PJP-ID</div>
        <span className="v">{memberNum}</span>
        <div className="k mt-1.5">STATUS</div>
        <span className={`v ${user?.faa_verified ? "green" : "orange"} cockpit-hud__flicker`}>
          {user?.faa_verified ? "VERIFIED" : "PENDING"}
        </span>
      </div>

      <div className="cockpit-hud__readout cockpit-hud__readout--tr">
        <div className="k">ZULU</div>
        <span className="v">{zulu}</span>
        <div className="k mt-1.5">HDG</div>
        <span className="v">{heading}°</span>
      </div>

      <div className="cockpit-hud__readout cockpit-hud__readout--bl">
        <div className="k">ALT</div>
        <span className="v">{altitude}</span>
        <div className="k mt-1.5">SPD</div>
        <span className="v">{speed}</span>
      </div>

      {/* Layer 4 — radar sweep bottom right */}
      <div className="cockpit-hud__radar">
        <div className="cockpit-hud__radar-label">RADAR · PJP</div>
        <span className="cockpit-hud__radar-dot d1" />
        <span className="cockpit-hud__radar-dot d2" />
        <span className="cockpit-hud__radar-dot d3" />
      </div>

      {/* Layer 5 — bottom telemetry ticker */}
      <div className="cockpit-hud__ticker">
        <div className="cockpit-hud__ticker-track">
          <span>PJP NET · <em>ONLINE</em></span>
          <span>MEMBERS · <em>26,511</em></span>
          <span>ADS-B FEED · <em>LIVE</em></span>
          <span>FAA REGISTRY · <em>591,782 AIRMEN</em></span>
          <span>AOG WATCH · <s>3 ACTIVE</s></span>
          <span>MARKETPLACE · <em>187 LISTINGS</em></span>
          <span>OPS TEMPO · <em>NOMINAL</em></span>
          <span>HOT MIC · <em>ACTIVE</em></span>
          <span>PJP FUEL · <em>ARMED</em></span>
          <span>PJP NET · <em>ONLINE</em></span>
          <span>MEMBERS · <em>26,511</em></span>
          <span>ADS-B FEED · <em>LIVE</em></span>
          <span>FAA REGISTRY · <em>591,782 AIRMEN</em></span>
          <span>AOG WATCH · <s>3 ACTIVE</s></span>
          <span>MARKETPLACE · <em>187 LISTINGS</em></span>
          <span>OPS TEMPO · <em>NOMINAL</em></span>
          <span>HOT MIC · <em>ACTIVE</em></span>
          <span>PJP FUEL · <em>ARMED</em></span>
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
 * JobRadarCard
 *
 * AeroIntel's per-member surface for open contracts that fit their type
 * ratings + location + availability. One click → AeroIntel chat pre-seeded
 * with a "why do I fit this posting?" question. Auto-hides for members
 * whose role has no meaningful match (charter_operator, admin).
 * =========================================================================*/
function JobRadarCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/aerointel/job-radar?limit=5");
      setData(r.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Skip for operator/admin roles — they post, they don't apply.
  if (["charter_operator", "operator", "admin"].includes(user?.role)) return null;
  if (loading) {
    return (
      <div className="mt-6 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4" data-testid="job-radar-loading">
        <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80 flex items-center gap-1.5">
          <Radar className="h-3.5 w-3.5" /> // AEROINTEL · Job Radar
        </div>
        <div className="mt-2 flex items-center gap-2 text-white/50 text-sm">
          <Loader2 className="h-3 w-3 animate-spin" /> Scanning open contracts…
        </div>
      </div>
    );
  }
  const matches = data?.matches || [];
  if (matches.length === 0) return null;

  return (
    <div className="mt-6 rounded-xl border border-cyan/25 bg-cyan/[0.04] p-4" data-testid="job-radar">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80 flex items-center gap-1.5">
          <Radar className="h-3.5 w-3.5" /> // AEROINTEL · Job Radar
        </div>
        <div className="mono text-[9px] tracking-widest uppercase text-white/45">
          {data.total_open} open contract{data.total_open !== 1 ? "s" : ""} · scored against your profile
        </div>
      </div>
      <div className="font-display text-xl font-bold text-white mt-1">
        {matches.length} match{matches.length !== 1 ? "es" : ""} you should look at right now.
      </div>
      <div className="mt-3 space-y-2">
        {matches.map((m) => (
          <div
            key={m.contract_id}
            className={`rounded-lg border p-3 flex items-center justify-between gap-3 flex-wrap ${m.already_applied ? "border-white/10 bg-obsidian/40 opacity-70" : "border-white/10 bg-obsidian/60"}`}
            data-testid={`job-radar-row-${m.contract_id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`mono text-[10px] tracking-widest uppercase rounded px-1.5 py-0.5 ${m.score >= 60 ? "text-avail bg-avail/10 border border-avail/40" : m.score >= 30 ? "text-cyan bg-cyan/10 border border-cyan/40" : "text-white/60 border border-white/15"}`}>
                  {m.score}pt
                </span>
                <span className="font-display text-base font-bold text-white truncate">{m.title || "Contract"}</span>
                {m.already_applied && <span className="mono text-[9px] tracking-widest uppercase text-white/45">· applied</span>}
              </div>
              <div className="mono text-[10px] tracking-widest uppercase text-white/55 mt-1 truncate">
                {m.base_icao || m.state || "—"} · {m.aircraft_types?.slice(0, 3).join(", ") || "no type filter"} · {m.application_count} applicant{m.application_count !== 1 ? "s" : ""}
              </div>
              {m.reasons?.length > 0 && (
                <div className="text-[12px] text-white/70 mt-1">{m.reasons.slice(0, 3).join(" · ")}</div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Link
                to={m.aerointel_deep_link}
                data-testid={`job-radar-ask-${m.contract_id}`}
                className="mono text-[10px] tracking-widest uppercase px-2.5 py-1.5 rounded border border-cyan/40 text-cyan hover:bg-cyan/10 inline-flex items-center gap-1.5"
              >
                <Sparkles className="h-3 w-3" /> ASK AEROINTEL
              </Link>
              <Link
                to={`/jobs/${m.contract_id}`}
                data-testid={`job-radar-open-${m.contract_id}`}
                className="btn-hud btn-hud-solid text-[10px]"
              >
                OPEN <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </div>
        ))}
      </div>
      {!data.member?.type_ratings_count && (
        <div className="mt-3 rounded border border-amber-400/30 bg-amber-400/[0.06] p-2.5 text-[12px] text-amber-200/90" data-testid="job-radar-fix-types">
          Add your FAA type ratings on your profile to unlock aircraft-specific matches — every rating you list scores +30pt per contract.
        </div>
      )}
    </div>
  );
}


/**
 * RoleSwitcher — pill row that lets a multi-role user flip the cockpit
 * between their available hats (e.g. pilot ↔ charter_operator). Admins
 * see the full role library so they can QA every surface. UI-only: the
 * DB `user.role` never changes; API permissions still key off the
 * persisted role.
 */
const ROLE_META = {
  pilot:              { emoji: "🛩️",  label: "Pilot",              rgb: "75,156,255"  },
  flight_attendant:   { emoji: "✈️",  label: "Flight Attendant",   rgb: "236,72,153"  },
  amt:                { emoji: "🔧",  label: "AMT / Mechanic",     rgb: "251,146,60"  },
  fbo:                { emoji: "⛽",  label: "FBO",                rgb: "255,209,102" },
  charter_operator:   { emoji: "🛫",  label: "Operator",           rgb: "46,213,115"  },
  aircraft_owner:     { emoji: "🛩",   label: "Owner",              rgb: "212,175,55"  },
  aircraft_sales:     { emoji: "🏷️",  label: "Sales",              rgb: "191,124,255" },
  charter_client:     { emoji: "💼",  label: "Charter Client",     rgb: "45,212,191"  },
  enthusiast:         { emoji: "🎯",  label: "Enthusiast",         rgb: "126,200,255" },
};

function RoleSwitcher({ roles, current, onChange, nativeRole }) {
  return (
    <div
      className="mb-4 rounded-2xl px-3 py-2 border border-white/[0.08] overflow-hidden"
      style={{
        background: "linear-gradient(135deg, rgba(75,156,255,0.08) 0%, rgba(5,8,16,0.55) 60%)",
        backdropFilter: "blur(18px) saturate(150%)",
        WebkitBackdropFilter: "blur(18px) saturate(150%)",
        boxShadow: "0 1px 0 rgba(255,255,255,0.05) inset, 0 12px 28px -14px rgba(0,0,0,0.55)",
      }}
      data-testid="cockpit-role-switcher"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80 flex items-center gap-2 flex-shrink-0">
          <span className="h-1.5 w-1.5 rounded-full bg-cyan animate-pulse" />
          // VIEW AS
        </div>
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar flex-1 min-w-0">
          {roles.map((r) => {
            const meta = ROLE_META[r] || { emoji: "•", label: r.replace(/_/g, " "), rgb: "255,255,255" };
            const isActive = current === r;
            const isNative = r === nativeRole;
            return (
              <button
                key={r}
                type="button"
                onClick={() => onChange(r)}
                data-testid={`cockpit-role-${r}`}
                aria-pressed={isActive}
                title={isNative ? `${meta.label} (your primary role)` : `View cockpit as ${meta.label}`}
                className={`relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mono text-[10.5px] tracking-widest uppercase transition-all flex-shrink-0 ${
                  isActive ? "text-white" : "text-white/70 hover:text-white"
                }`}
                style={
                  isActive
                    ? {
                        background: `linear-gradient(90deg, rgba(${meta.rgb},0.28), rgba(${meta.rgb},0.10))`,
                        border: `1px solid rgba(${meta.rgb},0.65)`,
                        boxShadow: `0 0 16px rgba(${meta.rgb},0.50), inset 0 0 12px rgba(${meta.rgb},0.10)`,
                      }
                    : {
                        background: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }
                }
              >
                <span className="text-sm leading-none">{meta.emoji}</span>
                <span>{meta.label}</span>
                {isNative && (
                  <span
                    className="ml-0.5 mono text-[8.5px] tracking-widest text-white/70 border border-white/20 px-1 rounded"
                    title="Your primary role"
                  >
                    OWN
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * MembershipCard — live subscription state pane inside the Cockpit.
 * Shows tier + next renewal, an ALWAYS-ON auto-renew toggle (flipping
 * off sets `cancel_at_period_end=true` on Stripe), and a cancel CTA.
 * Non-paying members see the traditional upsell into /membership.
 */
function MembershipCard({ user }) {
  const [sub, setSub] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/billing/subscription");
      setSub(r.data);
    } catch { setSub({ membership_status: "none" }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // A member counts as "paid" any time they have an active membership_status,
  // whether or not Stripe has a live subscription id for them (comped
  // accounts, legacy claims, founding members with no card on file).
  const activeMember = (sub?.membership_status === "active") || (user?.membership_status === "active");
  const hasStripeSub = !!sub?.stripe_subscription_id;
  const autoRenew = hasStripeSub ? !sub.subscription_cancel_at_period_end : false;
  const renewalDate = sub?.subscription_current_period_end
    ? new Date(sub.subscription_current_period_end).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : null;

  // Friendly tier labels — replaces raw slugs like "EMPTY_LEG_RIDER" with
  // marketing-clean names so the cancel/status card reads well.
  const TIER_LABELS = {
    pjp_pro: "PJP Pro",
    charter_operator: "Charter Operator",
    charter_client: "Charter Client",
    empty_leg_rider: "JumpSeat Member",
    aircraft_owner: "Aircraft Owner",
    aircraft_sales: "Aircraft Sales",
    founding: "Founding Member",
  };
  const rawTier = sub?.membership_tier_slug || user?.membership_tier_slug || "";
  const tierLabel = TIER_LABELS[rawTier] || (rawTier ? rawTier.replace(/_/g, " ") : "Member");
  const priceLine = sub?.subscription_price_amount
    ? `$${(sub.subscription_price_amount / 100).toLocaleString()} / year`
    : null;

  const doCancel = async () => {
    setSaving(true);
    try {
      await api.post("/billing/cancel");
      toast.success(renewalDate ? `Cancelled — access continues until ${renewalDate}.` : "Cancelled — auto-renew is off.");
      setConfirmOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed. Please contact support@privatejetpilots.com.");
    } finally { setSaving(false); }
  };

  const doReactivate = async () => {
    setSaving(true);
    try {
      await api.post("/billing/reactivate");
      toast.success("Auto-renew back on — welcome back.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reactivate failed.");
    } finally { setSaving(false); }
  };

  // Truly unpaid — only then do we show the upsell.
  if (!activeMember) {
    return (
      <Link
        to="/membership"
        data-testid="cockpit-membership-cta"
        className="group relative block rounded-2xl overflow-hidden isolate transition-transform hover:-translate-y-0.5"
      >
        <div
          className="relative rounded-2xl p-5 border border-white/10 group-hover:border-cyan/50 backdrop-blur-2xl backdrop-saturate-150"
          style={{ background: "linear-gradient(155deg, rgba(75,156,255,0.10) 0%, rgba(5,8,16,0.65) 60%)" }}
        >
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// TIERS & BILLING</div>
          <div className="flex items-center justify-between gap-4 mt-1 flex-wrap">
            <div className="min-w-0">
              <div className="font-display text-xl md:text-2xl font-bold text-white">Join PJP</div>
              <div className="text-sm text-white/60 mt-1">
                Verified crew network, JumpSeat access, PJP Wallet and more from $249/year.
              </div>
            </div>
            <span className="btn-hud btn-hud-solid flex-shrink-0">
              View Plans <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </div>
        </div>
      </Link>
    );
  }

  const cancelPending = hasStripeSub && !autoRenew;

  return (
    <>
    <div
      className="relative rounded-2xl overflow-hidden isolate border border-avail/40 backdrop-blur-2xl backdrop-saturate-150"
      style={{
        background: "linear-gradient(155deg, rgba(46,213,115,0.10) 0%, rgba(5,8,16,0.85) 60%)",
        boxShadow: "0 1px 0 rgba(255,255,255,0.06) inset, 0 20px 50px -20px rgba(0,0,0,0.55), 0 0 80px -40px rgba(46,213,115,0.55)",
      }}
      data-testid="cockpit-membership-card"
    >
      <span
        aria-hidden="true"
        className="absolute top-0 left-6 right-6 h-px pointer-events-none"
        style={{ background: "linear-gradient(90deg, transparent, rgba(46,213,115,0.85), transparent)" }}
      />
      <div className="p-5 md:p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="mono text-[10px] tracking-[0.24em] uppercase text-avail flex items-center gap-2 flex-wrap">
              // MEMBERSHIP · ACTIVE
              <span className="inline-flex items-center gap-1 mono text-[9px] tracking-widest uppercase text-avail bg-avail/[0.10] border border-avail/40 px-2 py-0.5 rounded-full">
                <ShieldCheck className="h-2.5 w-2.5" /> {tierLabel.toUpperCase()}
              </span>
            </div>
            <div className="font-display text-xl md:text-2xl font-bold text-white mt-1">You're all set.</div>
            <div className="text-sm text-white/70 mt-1">
              {cancelPending ? (
                <>Auto-renew is <span className="text-amber-300 font-semibold">off</span>. Your access ends {renewalDate || "at the period end"}.</>
              ) : hasStripeSub && renewalDate ? (
                <>Renews <span className="text-white font-semibold">{renewalDate}</span>{priceLine ? ` · ${priceLine}` : ""}.</>
              ) : hasStripeSub ? "Your subscription is active."
              : "Comped access — no card on file. Contact support to convert to a paid tier."}
            </div>
          </div>
          <Link to="/membership" data-testid="cockpit-membership-view-plans" className="mono text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-white/15 text-white/70 hover:text-white hover:border-white/30 flex-shrink-0">
            Compare Plans <ArrowRight className="h-3 w-3 inline ml-0.5" />
          </Link>
        </div>

        {/* Auto-renew toggle — only when a Stripe subscription exists */}
        {hasStripeSub && (
          <>
            <div className="mt-5 flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-3">
              <div className="min-w-0">
                <div className="mono text-[10px] tracking-widest uppercase text-white/50">Auto-renew</div>
                <div className="text-[13.5px] text-white mt-0.5">
                  {autoRenew ? "On — we'll keep your access active seamlessly." : "Off — your membership will lapse at the next period end."}
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={autoRenew}
                onClick={() => autoRenew ? setConfirmOpen(true) : doReactivate()}
                disabled={saving}
                data-testid="cockpit-membership-auto-renew"
                className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors ${
                  autoRenew ? "bg-avail" : "bg-white/15"
                } disabled:opacity-60`}
                style={autoRenew ? { boxShadow: "0 0 12px rgba(46,213,115,0.55)" } : undefined}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${autoRenew ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>

            {autoRenew ? (
              <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                disabled={saving}
                data-testid="cockpit-membership-cancel"
                className="mt-3 mono text-[10px] tracking-widest uppercase text-red-300 hover:text-red-200 disabled:opacity-60"
              >
                Cancel membership
              </button>
            ) : (
              <button
                type="button"
                onClick={doReactivate}
                disabled={saving}
                data-testid="cockpit-membership-reactivate"
                className="mt-3 mono text-[10px] tracking-widest uppercase text-avail hover:text-white disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-3 w-3 animate-spin inline" /> : "Re-enable auto-renew"}
              </button>
            )}
          </>
        )}
      </div>
    </div>

    {/* In-page confirm modal — replaces window.confirm which was silently
        blocked on some mobile browsers so pilots couldn't cancel. */}
    {confirmOpen && (
      <div
        className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm grid place-items-center px-4"
        onClick={() => !saving && setConfirmOpen(false)}
        data-testid="cockpit-cancel-confirm"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md rounded-2xl border border-red-500/40 backdrop-blur-2xl p-5"
          style={{ background: "linear-gradient(155deg, rgba(220,38,38,0.10) 0%, rgba(5,8,16,0.92) 60%)" }}
        >
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-red-300">// CONFIRM CANCEL</div>
          <div className="font-display text-xl font-bold text-white mt-1">Cancel your PJP membership?</div>
          <div className="text-[13.5px] text-white/75 mt-3 leading-relaxed">
            Your access stays active until{" "}
            <span className="text-white font-semibold">{renewalDate || "the end of your current period"}</span>.
            After that, your membership won't renew. You can turn auto-renew back on any time before then.
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              disabled={saving}
              data-testid="cockpit-cancel-confirm-keep"
              className="mono text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-white/15 text-white/70 hover:text-white"
            >
              Keep membership
            </button>
            <button
              type="button"
              onClick={doCancel}
              disabled={saving}
              data-testid="cockpit-cancel-confirm-yes"
              className="mono text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-red-500/60 bg-red-500/[0.14] text-red-200 hover:bg-red-500/[0.24]"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Yes, cancel"}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}


/**
 * DigitalIdCard — smart shortcut into the PJP Digital ID surface. If the
 * pilot already owns an active badge, we skip the sales pitch and drop
 * them into build / share; if not, we still sell it.
 */
function DigitalIdCard({ user }) {
  const owned = user?.pjp_id_status === "active" || !!user?.pjp_id?.public_token;
  const steps = [
    { n: 1, label: "Upload your ID photo" },
    { n: 2, label: "Confirm shipping address for the physical NFC card" },
    { n: 3, label: "Preview & activate your badge" },
  ];

  if (owned) {
    return (
      <Link
        to="/digital-id"
        data-testid="cockpit-id-cta"
        className="group relative block rounded-2xl overflow-hidden isolate transition-transform hover:-translate-y-0.5"
      >
        <div
          className="relative rounded-2xl p-5 border border-avail/40 group-hover:border-avail/70 backdrop-blur-2xl backdrop-saturate-150 overflow-hidden"
          style={{ background: "linear-gradient(155deg, rgba(46,213,115,0.10) 0%, rgba(5,8,16,0.85) 60%)" }}
        >
          <span
            aria-hidden="true"
            className="absolute top-0 left-6 right-6 h-px pointer-events-none"
            style={{ background: "linear-gradient(90deg, transparent, rgba(46,213,115,0.85), transparent)" }}
          />
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-avail flex items-center gap-2">
            // DIGITAL ID · YOURS
            <span className="mono text-[9px] tracking-widest uppercase text-avail bg-avail/[0.10] border border-avail/40 px-2 py-0.5 rounded-full">ACTIVE</span>
          </div>
          <div className="flex items-center justify-between gap-4 mt-1 flex-wrap">
            <div className="min-w-0">
              <div className="font-display text-xl md:text-2xl font-bold text-white">Add PJP ID to your account</div>
              <div className="text-sm text-white/70 mt-1">Finish building your badge in 3 steps — photo, address, activate. Share the animated card or tap NFC at the FBO.</div>
              <ol className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 max-w-xl">
                {steps.map((s) => (
                  <li key={s.n} className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
                    <div className="mono text-[9px] tracking-widest uppercase text-avail">STEP {s.n}</div>
                    <div className="text-[12px] text-white/80 mt-0.5 leading-snug">{s.label}</div>
                  </li>
                ))}
              </ol>
            </div>
            <span className="btn-hud btn-hud-solid flex-shrink-0 group-hover:brightness-110">
              Build My Badge <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to="/digital-id"
      data-testid="cockpit-id-cta"
      className="group relative block rounded-2xl overflow-hidden isolate transition-transform hover:-translate-y-0.5"
    >
      <div
        className="relative rounded-2xl p-5 border border-white/10 group-hover:border-cyan/50 backdrop-blur-2xl backdrop-saturate-150"
        style={{ background: "linear-gradient(155deg, rgba(75,156,255,0.10) 0%, rgba(5,8,16,0.65) 60%)" }}
      >
        <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// NFC BADGE · $199/yr</div>
        <div className="flex items-center justify-between gap-4 mt-1 flex-wrap">
          <div className="min-w-0">
            <div className="font-display text-xl md:text-2xl font-bold text-white">PJP Digital ID</div>
            <div className="text-sm text-white/60 mt-1">Animated pilot badge + physical NFC card. Tap to verify at any FBO in the world.</div>
          </div>
          <span className="btn-hud btn-hud-solid flex-shrink-0">
            Get My ID <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}


